
import React from 'react';
import type { StaffMember, Department, DayOfWeek, AvailabilityStatus } from '../../types';
import { ALL_DAYS } from '../../types';
import Modal from '../ui/Modal';
import { Icons } from '../ui/Icons';

interface StaffProfileModalProps {
    staff: StaffMember;
    departments: Department[];
    onClose: () => void;
}

const statusStyles: { [key in AvailabilityStatus]: string } = {
    Available: 'bg-green-100 text-green-800 dark:bg-green-900/25 dark:text-green-300',
    Preferred: 'bg-blue-100 text-blue-800 dark:bg-blue-900/25 dark:text-blue-300',
    Unavailable: 'bg-red-100 text-red-800 dark:bg-red-900/25 dark:text-red-300',
};

const getInitials = (name?: string) => {
    if (!name) return '?';
    const names = name.split(' ');
    if (names.length > 1 && names[names.length - 1]) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const StaffProfileModal: React.FC<StaffProfileModalProps> = ({ staff, departments, onClose }) => {
    const staffDepartments = departments.filter(d => staff.departmentIds?.includes(d.id));
    return (
        <Modal title="Staff Profile" onClose={onClose}>
            <div className="p-6">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                    <div className="flex-shrink-0">
                        <div
                            className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold border-4 border-white dark:border-slate-800 shadow-md"
                            style={{ backgroundColor: staff.color }}
                        >
                            {getInitials(staff.name)}
                        </div>
                    </div>
                    <div className="flex-grow text-center sm:text-left">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{staff.name}</h2>
                        <p className="text-md text-gray-600 dark:text-gray-400">{staff.role}</p>
                        <div className="mt-2 flex flex-wrap justify-center sm:justify-start gap-2">
                            {staffDepartments.map((dep, index) => (
                                <span
                                    key={dep.id}
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${index === 0 ? 'ring-2 ring-offset-1 dark:ring-offset-slate-800 ring-indigo-500' : ''}`}
                                    style={{ backgroundColor: `${dep.color}20`, color: dep.color }}
                                >
                                    {dep.name}
                                    {index === 0 && <span className="ml-1 text-[8px] uppercase font-bold opacity-70">(Primary)</span>}
                                </span>
                            ))}
                        </div>
                        {staff.isDriver && (
                            <span className="mt-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                                <Icons.Bus className="w-3 h-3" />
                                Driver
                            </span>
                        )}
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact Information</h3>
                        <div className="mt-2 space-y-2 text-gray-900 dark:text-gray-200">
                            <div className="flex items-center gap-3">
                                <Icons.Mail className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span>{staff.email}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Icons.Phone className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span>{staff.phone}</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Notes</h3>
                        <p className="mt-2 text-gray-700 dark:text-gray-300 italic">
                            {staff.notes || 'No notes yet.'}
                        </p>
                    </div>
                </div>

                <div className="mt-8">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Weekly Availability</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-center">
                        {ALL_DAYS.map(day => (
                            <div key={day} className="p-2 rounded-lg bg-gray-50 dark:bg-slate-700/50 border dark:border-slate-700">
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{day.substring(0, 3)}</p>
                                <p className={`mt-1 text-xs font-bold px-2 py-0.5 rounded-full inline-block ${statusStyles[(staff.availability && staff.availability[day]) || 'Available']}`}>
                                    {(staff.availability && staff.availability[day]) || 'Available'}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t dark:border-slate-700">
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:w-auto sm:text-sm"
                >
                    Close
                </button>
            </div>
        </Modal>
    );
};

export default StaffProfileModal;